import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { TableTopExercise, TableTopExerciseUpdate } from '../../types/tabletopExercise';
import { usePartnerTokenTiers } from '../../contexts/PartnerTokenTiersContext';
import {
  api,
  generateTabletopBlankForm,
  getTabletopBlankFormStatus,
  uploadTabletopCompletedDocument,
} from '../../services/api';
import TabletopAppointmentSection from './TabletopAppointmentSection';

const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
  pending: { bg: '#FEF3C7', text: '#92400E', dot: '#F59E0B' },
  approved: { bg: '#D1FAE5', text: '#065F46', dot: '#10B981' },
  disabled: { bg: '#F1F5F9', text: '#94A3B8', dot: '#CBD5E1' },
  ready: { bg: '#ECFDF5', text: '#065F46', dot: '#10B981' },
  draft: { bg: '#FEF3C7', text: '#92400E', dot: '#F59E0B' },
  scheduled: { bg: '#DBEAFE', text: '#1E40AF', dot: '#3B82F6' },
  in_progress: { bg: '#FEF3C7', text: '#92400E', dot: '#F59E0B' },
  completed: { bg: '#D1FAE5', text: '#065F46', dot: '#10B981' },
  submitted_for_audit: { bg: '#EDE9FE', text: '#6D28D9', dot: '#8B5CF6' },
  cancelled: { bg: '#FEE2E2', text: '#991B1B', dot: '#EF4444' },
};

const StatusBadge: React.FC<{ status: string; label: string }> = ({ status, label }) => {
  const s = statusColors[status] || statusColors.pending;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
      style={{ backgroundColor: s.bg, color: s.text }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.dot }} />
      {label}
    </span>
  );
};

const ExerciseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { tokenTitles } = usePartnerTokenTiers();
  const [exercise, setExercise] = useState<TableTopExercise | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingForm, setGeneratingForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [auditRequested, setAuditRequested] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modals state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddParticipantModalOpen, setIsAddParticipantModalOpen] = useState(false);
  const [isScenarioModalOpen, setIsScenarioModalOpen] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editScheduledDate, setEditScheduledDate] = useState('');
  const [editBusinessName, setEditBusinessName] = useState('');
  const [editEmployeeCount, setEditEmployeeCount] = useState<number | string>(10);
  const [editSystems, setEditSystems] = useState('');
  const [editScenarioDesc, setEditScenarioDesc] = useState('');

  // Add Participant state
  const [newPartName, setNewPartName] = useState('');
  const [newPartRole, setNewPartRole] = useState('');
  const [newPartEmail, setNewPartEmail] = useState('');
  const [newPartPhone, setNewPartPhone] = useState('');

  const auditIncludedInToken =
    exercise?.partner_token_tier === 'gold' || exercise?.partner_token_tier === 'premium';

  const auditSubmissionLocked =
    exercise?.status === 'submitted_for_audit' ||
    Boolean(exercise?.uploaded_document_url && exercise?.audit_requested);

  useEffect(() => {
    if (id) fetchExercise();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // If blank form is being generated in background, poll for completion
  useEffect(() => {
    if (!id || !exercise || exercise.blank_form_url) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const s = await getTabletopBlankFormStatus(Number(id));
        if (!cancelled && s.status === 'ready') {
          await fetchExercise();
          return;
        }
      } catch {
        // keep polling
      }
      if (attempts < 20 && !cancelled) {
        setTimeout(tick, 2000);
      }
    };
    const initial = setTimeout(tick, 1500);
    return () => {
      cancelled = true;
      clearTimeout(initial);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, exercise?.id, exercise?.blank_form_url]);

  const fetchExercise = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get<TableTopExercise>(`/v1/tabletop-exercises/${id}`);
      setExercise(data);
      const tier = data.partner_token_tier;
      if (tier === 'gold' || tier === 'premium' || data.audit_requested) {
        setAuditRequested(true);
      }
    } catch (err) {
      setError('Failed to fetch exercise details');
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = () => {
    if (!exercise) return;
    setEditName(exercise.exercise_name || '');
    setEditScheduledDate(
      exercise.scheduled_date
        ? new Date(exercise.scheduled_date).toISOString().slice(0, 16)
        : ''
    );
    setEditBusinessName(
      exercise.business_context?.business_name ||
        (exercise as any).bcp_name ||
        'Vic Vic Ltd'
    );
    setEditEmployeeCount(exercise.business_context?.employee_count || 10);
    const systems = exercise.business_context?.key_systems;
    if (Array.isArray(systems) && systems.length > 0) {
      const names = systems.map((s) => (typeof s === 'string' ? s : s?.name)).filter(Boolean);
      setEditSystems(names.join(', '));
    } else {
      setEditSystems('ERP, CRM');
    }
    setEditScenarioDesc(exercise.scenario_description || '');
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !exercise) return;

    setIsSavingEdit(true);
    try {
      const parsedSystems = editSystems
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const updatePayload: TableTopExerciseUpdate = {
        exercise_name: editName.trim(),
        scheduled_date: editScheduledDate ? new Date(editScheduledDate).toISOString() : undefined,
        scenario_description: editScenarioDesc.trim(),
        business_context: {
          business_name: editBusinessName.trim() || 'Vic Vic Ltd',
          employee_count: Number(editEmployeeCount) || 10,
          key_systems: parsedSystems,
        },
      };

      await api.put(`/v1/tabletop-exercises/${id}`, updatePayload);
      await fetchExercise();
      setIsEditModalOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update exercise');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleAddParticipant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !exercise || !newPartName.trim()) return;

    try {
      const currentParticipants = Array.isArray(exercise.participants)
        ? [...exercise.participants]
        : [];
      const newParticipant = {
        name: newPartName.trim(),
        role: newPartRole.trim() || 'Exercise participant',
        email: newPartEmail.trim() || undefined,
        phone: newPartPhone.trim() || undefined,
      };

      const updated = [...currentParticipants, newParticipant];
      await api.put(`/v1/tabletop-exercises/${id}`, { participants: updated });
      await fetchExercise();
      setNewPartName('');
      setNewPartRole('');
      setNewPartEmail('');
      setNewPartPhone('');
      setIsAddParticipantModalOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add participant');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !id || auditSubmissionLocked) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PDF and Word documents are allowed');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadSuccess(null);

    try {
      await uploadTabletopCompletedDocument(
        Number(id),
        file,
        auditIncludedInToken || auditRequested
      );
      setUploadSuccess(
        auditIncludedInToken || auditRequested
          ? `Document "${file.name}" uploaded — Flash assessment requested.`
          : `Document "${file.name}" uploaded successfully.`
      );
      fetchExercise();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleGenerateBlankForm = async () => {
    if (!id || generatingForm || auditSubmissionLocked) return;
    setGeneratingForm(true);
    setError(null);
    try {
      const data = await generateTabletopBlankForm(Number(id));
      if (data.status === 'already_exists') {
        await fetchExercise();
        await downloadBlankForm();
        setGeneratingForm(false);
        return;
      }
      let attempts = 0;
      const max = 60;
      const tick = async () => {
        try {
          const s = await getTabletopBlankFormStatus(Number(id));
          if (s.status === 'ready') {
            setGeneratingForm(false);
            await fetchExercise();
            await downloadBlankForm();
          } else if (attempts < max) {
            attempts += 1;
            setTimeout(tick, 1000);
          } else {
            setError('Blank form generation timed out');
            setGeneratingForm(false);
          }
        } catch {
          setError('Error checking blank form status');
          setGeneratingForm(false);
        }
      };
      tick();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate blank form');
      setGeneratingForm(false);
    }
  };

  const downloadBlankForm = async () => {
    if (auditSubmissionLocked) return;
    try {
      const res = await api.get(`/v1/tabletop-exercises/${id}/blank-form-file/`, {
        responseType: 'blob',
      });
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const filename =
        res.headers['content-disposition']?.match(/filename="?([^";]+)"?/)?.[1] ||
        `exercise_${id}_tabletop_form.docx`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Blank form download failed');
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatScheduledDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not scheduled';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'Not scheduled';
    const datePart = d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    // Check if time is 00:00:00Z or date-only
    if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && dateString.endsWith('T00:00:00Z')) {
      return `${datePart} · 9:09 PM`;
    }
    const timePart = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${datePart} · ${timePart}`;
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatLabel = (value: string) =>
    value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200/80 shadow-xs">
            <div className="w-10 h-10 border-3 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm font-medium text-slate-500">Loading exercise details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !exercise) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl p-10 text-center border border-slate-200/80 shadow-xs">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Exercise Not Found</h2>
            <p className="text-sm text-slate-500 mb-6">{error}</p>
            <Link
              to="/tabletop-exercises"
              className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors"
            >
              Back to Exercises
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!exercise) return null;

  const statusLabel =
    exercise.status === 'submitted_for_audit'
      ? 'Submitted for Assessment'
      : exercise.status === 'completed'
      ? 'Completed'
      : exercise.status === 'in_progress'
      ? 'In Progress'
      : exercise.status === 'scheduled'
      ? 'Scheduled'
      : exercise.status === 'cancelled'
      ? 'Cancelled'
      : 'Not started';

  const categoryName = formatLabel(
    exercise.scenario_subcategory || exercise.exercise_type || exercise.scenario_category || 'Phishing'
  );

  const businessName =
    exercise.business_context?.business_name ||
    (exercise as any).bcp_name ||
    (exercise as any).company_name ||
    'Vic Vic Ltd';

  const employeeCount = exercise.business_context?.employee_count || 10;

  const systemsInScope = (() => {
    const raw = exercise.business_context?.key_systems;
    if (Array.isArray(raw) && raw.length > 0) {
      const names = raw.map((s) => (typeof s === 'string' ? s : s?.name)).filter(Boolean);
      if (names.length > 0) return names.join(', ');
    }
    return 'ERP, CRM';
  })();

  const participantsList = Array.isArray(exercise.participants) && exercise.participants.length > 0
    ? exercise.participants.map((p: any) =>
        typeof p === 'string'
          ? { name: p, role: 'Participant', email: '', phone: '' }
          : p
      )
    : [
        {
          name: 'Mark Doe',
          role: 'CTO',
          email: 'chigozievnwafor@gmail.com',
          phone: '(431) 277-4478',
        },
      ];

  const completedSteps = [
    Boolean(exercise.blank_form_url),
    Boolean(
      exercise.status === 'in_progress' ||
        exercise.status === 'completed' ||
        exercise.uploaded_document_url
    ),
    Boolean(exercise.uploaded_document_url || exercise.status === 'completed'),
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        
        {/* Back Link */}
        <div className="mb-4">
          <Link
            to="/tabletop-exercises"
            className="text-xs font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1.5 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
            </svg>
            All exercises
          </Link>
        </div>

        {/* Top Badges & Edit Button Row */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="bg-slate-100 text-slate-700 font-semibold px-2.5 py-1 rounded-md text-xs">
              {categoryName}
            </span>
            <StatusBadge status={exercise.status} label={statusLabel} />
          </div>

          <button
            type="button"
            onClick={openEditModal}
            className="bg-white border border-slate-200/90 text-slate-800 hover:bg-slate-50 font-semibold text-xs px-3.5 py-1.5 rounded-lg shadow-2xs flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <svg className="w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Edit exercise
          </button>
        </div>

        {/* Exercise Title */}
        <h1 className="text-3xl font-extrabold text-[#0F172A] tracking-tight mt-1 mb-6">
          {exercise.exercise_name}
        </h1>

        {/* 4 Metadata Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs">
            <div className="text-xs text-slate-500 font-medium mb-1">Scheduled for</div>
            <div className="text-sm font-bold text-slate-900">
              {formatScheduledDateTime(exercise.scheduled_date)}
            </div>
          </div>

          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs">
            <div className="text-xs text-slate-500 font-medium mb-1">Business</div>
            <div className="text-sm font-bold text-slate-900">
              {businessName} · {employeeCount} people
            </div>
          </div>

          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs">
            <div className="text-xs text-slate-500 font-medium mb-1">Systems in scope</div>
            <div className="text-sm font-bold text-slate-900 truncate" title={systemsInScope}>
              {systemsInScope}
            </div>
          </div>

          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs">
            <div className="text-xs text-slate-500 font-medium mb-1">Created</div>
            <div className="text-sm font-bold text-slate-900">
              {formatDate(exercise.created_at) || 'Jun 13, 2026'}
            </div>
          </div>
        </div>

        {/* Global Error Banner */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-3.5 flex items-center justify-between text-xs text-red-700">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 font-bold ml-2">
              ✕
            </button>
          </div>
        )}

        {/* Main Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* Left Column: Your next steps */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-base font-bold text-slate-900">Your next steps</h2>
                <span className="sr-only">Exercise workflow</span>
                <span className="text-xs font-semibold text-slate-500">{completedSteps} of 3 done</span>
              </div>

              {/* Vertical Stepper */}
              <div className="space-y-0">
                
                {/* Step 1 */}
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-7 h-7 rounded-full bg-[#991B1B] text-white flex items-center justify-center text-xs font-bold shrink-0">
                      1
                    </div>
                    <div className="w-0.5 bg-slate-200 h-14 my-1" />
                  </div>
                  <div className="flex-1 pb-4">
                    <h3 className="text-sm font-bold text-slate-900">Download the tabletop worksheet</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Pre-filled with the scenario, injects and discussion questions. Generated{' '}
                      {formatDate(exercise.blank_form_generated_at) || formatDate(exercise.created_at) || 'Jun 14, 2026'}.
                    </p>
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={exercise.blank_form_url ? downloadBlankForm : handleGenerateBlankForm}
                        disabled={auditSubmissionLocked || generatingForm}
                        aria-label="Download form"
                        className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white text-xs font-bold px-4 py-2 rounded-lg shadow-xs inline-flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {generatingForm ? 'Generating worksheet...' : 'Download worksheet'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-7 h-7 rounded-full border border-slate-300 text-slate-500 flex items-center justify-center text-xs font-semibold shrink-0">
                      2
                    </div>
                    <div className="w-0.5 bg-slate-200 h-14 my-1" />
                  </div>
                  <div className="flex-1 pb-4">
                    <h3 className="text-sm font-bold text-slate-900">Run the exercise with your team</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Walk through the scenario together and record decisions, response times, gaps and actions on the worksheet.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-7 h-7 rounded-full border border-slate-300 text-slate-500 flex items-center justify-center text-xs font-semibold shrink-0">
                      3
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-slate-900">Upload the completed worksheet</h3>
                    <p className="text-xs text-slate-500 mt-0.5">PDF or Word.</p>
                    <p className="text-xs text-slate-400 mt-1">Unlocks after you've run the exercise.</p>

                    {/* Step 3 Action Details / Upload */}
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                      <p className="text-xs text-slate-600 leading-relaxed">
                        {auditSubmissionLocked
                          ? 'Your completed results have been submitted. Flash will review your worksheet against ISO 22301:2019. This is not a certification.'
                          : auditIncludedInToken
                          ? 'Upload the completed worksheet (PDF or Word). Your token includes an ISO 22301 alignment review. Submission notifies Flash automatically.'
                          : 'Upload the completed worksheet (PDF or Word). You can also request an ISO 22301 alignment review.'}
                      </p>

                      <label className="flex items-center gap-2.5 text-xs text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={auditRequested}
                          onChange={(e) => setAuditRequested(e.target.checked)}
                          disabled={auditSubmissionLocked || auditIncludedInToken}
                          className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300"
                        />
                        <span>
                          Submit for an ISO 22301 alignment review
                          {auditIncludedInToken && exercise?.partner_token_tier
                            ? ` (included with ${tokenTitles[exercise.partner_token_tier] || (exercise.partner_token_tier === 'premium' ? 'Executive' : 'Prepared+')})`
                            : ''}
                        </span>
                      </label>

                      {auditRequested && !auditSubmissionLocked && (
                        <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-xl text-xs text-slate-600 space-y-2">
                          <p className="font-semibold text-slate-800">An ISO 22301:2019-assessed tabletop delivers:</p>
                          <ul className="list-disc pl-4 space-y-0.5 text-slate-600">
                            <li>Credible evidence the exercise was real and complete</li>
                            <li>Validation of BC strategies, communication, and leadership</li>
                            <li>Structured documentation that meets global assessment expectations</li>
                          </ul>
                        </div>
                      )}

                      {!auditSubmissionLocked && (
                        <div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.docx"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="upload-doc"
                          />
                          <label
                            htmlFor="upload-doc"
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-colors shadow-2xs ${
                              auditRequested
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'
                            } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            {uploading ? 'Uploading...' : auditRequested ? 'Submit for assessment' : 'Upload document'}
                          </label>
                        </div>
                      )}

                      {/* Upload feedback */}
                      {uploadSuccess && (
                        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
                          {uploadSuccess}
                        </div>
                      )}

                      {exercise.uploaded_document_url && (
                        <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-900">
                          Document uploaded {exercise.audit_requested ? '— Flash assessment requested' : '— Pending admin review'}
                          {exercise.uploaded_document_at ? ` on ${formatDate(exercise.uploaded_document_at)}` : ''}
                          {exercise.cowbell_notified_at ? ' — Cowbell notified ✓' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Live Review Session Booking with Rob */}
            <TabletopAppointmentSection
              exerciseId={exercise.id}
              exerciseName={exercise.exercise_name}
            />
          </div>

          {/* Right Column: Scenario & Participants */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Scenario Card */}
            <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs">
              <h2 aria-label="Scenario overview" className="text-base font-bold text-slate-900 mb-3">
                Scenario
              </h2>
              <p className="text-xs text-slate-600 leading-relaxed">
                {exercise.scenario_description}
              </p>
              <button
                type="button"
                onClick={() => setIsScenarioModalOpen(true)}
                className="text-xs font-semibold text-[#1D4ED8] hover:underline cursor-pointer inline-flex items-center gap-1 mt-3.5 bg-transparent border-none p-0"
              >
                Read full scenario
              </button>
            </div>

            {/* Participants Card */}
            <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-slate-900">
                  Participants {participantsList.length}
                </h2>
                <button
                  type="button"
                  onClick={() => setIsAddParticipantModalOpen(true)}
                  className="text-xs font-semibold text-[#1D4ED8] hover:underline cursor-pointer bg-transparent border-none p-0"
                >
                  + Add people
                </button>
              </div>

              <div className="space-y-3">
                {participantsList.map((participant, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#0F172A] text-white text-xs font-bold flex items-center justify-center shrink-0">
                        {getInitials(participant.name || 'User')}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-900">{participant.name || 'Unnamed'}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {participant.role
                            ? participant.role.toLowerCase().includes('lead')
                              ? participant.role
                              : `${participant.role} · Exercise lead`
                            : 'CTO · Exercise lead'}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {participant.email && (
                        <a
                          href={`mailto:${participant.email}`}
                          className="text-xs text-[#1D4ED8] hover:underline font-medium block"
                        >
                          {participant.email}
                        </a>
                      )}
                      {participant.phone && (
                        <div className="text-[11px] text-slate-500 mt-0.5">{participant.phone}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-[11px] text-slate-500 mt-4 leading-normal">
                Tip: invite everyone with a role in your continuity plan — leadership, IT, HR and communications.
              </p>
            </div>

            {/* Approval Status Card (Only if ISO audit requested) */}
            {exercise.audit_requested && (
              <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-2xs">
                <h2 className="text-base font-bold text-slate-900 mb-3">Approval status</h2>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                    <div className="text-xs font-semibold text-slate-500 mb-2">ISO assessor</div>
                    <StatusBadge
                      status={exercise.iso_auditor_approved ? 'approved' : 'pending'}
                      label={exercise.iso_auditor_approved ? 'Approved' : 'Pending'}
                    />
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                    <div className="text-xs font-semibold text-slate-500 mb-2">Document download</div>
                    <StatusBadge
                      status={exercise.document_download_enabled ? 'approved' : 'disabled'}
                      label={exercise.document_download_enabled ? 'Enabled' : 'Disabled'}
                    />
                  </div>
                </div>
                {!exercise.document_download_enabled && (
                  <p className="text-xs text-slate-400 mt-3">Download unlocks after assessor approval.</p>
                )}
              </div>
            )}

          </div>

        </div>

      </div>

      {/* Edit Exercise Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-xl border border-slate-200/80 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-5">
              <h2 className="text-lg font-bold text-slate-900">Edit Exercise</h2>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-lg font-bold p-1 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Exercise Name
                </label>
                <input
                  type="text"
                  required
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Scheduled Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={editScheduledDate}
                    onChange={(e) => setEditScheduledDate(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={editBusinessName}
                    onChange={(e) => setEditBusinessName(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Employee Count
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={editEmployeeCount}
                    onChange={(e) => setEditEmployeeCount(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Systems in Scope (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={editSystems}
                    onChange={(e) => setEditSystems(e.target.value)}
                    placeholder="ERP, CRM, Cloud Infrastructure"
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Scenario Description
                </label>
                <textarea
                  rows={4}
                  value={editScenarioDesc}
                  onChange={(e) => setEditScenarioDesc(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingEdit}
                  className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                >
                  {isSavingEdit ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Participant Modal */}
      {isAddParticipantModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-200/80">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
              <h2 className="text-base font-bold text-slate-900">Add Participant</h2>
              <button
                type="button"
                onClick={() => setIsAddParticipantModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-lg font-bold p-1 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddParticipant} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Jane Smith"
                  value={newPartName}
                  onChange={(e) => setNewPartName(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Role</label>
                <input
                  type="text"
                  placeholder="e.g. Head of Communications"
                  value={newPartRole}
                  onChange={(e) => setNewPartRole(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  placeholder="jane@example.com"
                  value={newPartEmail}
                  onChange={(e) => setNewPartEmail(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Phone</label>
                <input
                  type="tel"
                  placeholder="(555) 000-0000"
                  value={newPartPhone}
                  onChange={(e) => setNewPartPhone(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsAddParticipantModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors cursor-pointer"
                >
                  Add participant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Read Full Scenario Modal */}
      {isScenarioModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-xl border border-slate-200/80 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
              <div>
                <h2 className="text-base font-bold text-slate-900">{exercise.exercise_name}</h2>
                <div className="text-xs text-slate-500 mt-0.5">Full Scenario Details & Injects</div>
              </div>
              <button
                type="button"
                onClick={() => setIsScenarioModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-lg font-bold p-1 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-5 text-xs text-slate-700 leading-relaxed">
              <div>
                <div className="font-bold text-slate-900 mb-1.5 uppercase tracking-wider text-[11px]">
                  Scenario Description
                </div>
                <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 text-slate-700">
                  {exercise.scenario_description}
                </div>
              </div>

              {exercise.response_objectives && exercise.response_objectives.length > 0 && (
                <div>
                  <div className="font-bold text-slate-900 mb-1.5 uppercase tracking-wider text-[11px]">
                    Response Objectives
                  </div>
                  <div className="space-y-2">
                    {exercise.response_objectives.map((obj, i) => (
                      <div key={i} className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                        <div className="font-bold text-slate-900 flex items-center justify-between">
                          <span>{obj.title}</span>
                          {obj.iso_clause && (
                            <span className="text-[10px] bg-slate-200 text-slate-700 px-2 py-0.5 rounded font-mono">
                              ISO {obj.iso_clause}
                            </span>
                          )}
                        </div>
                        <p className="text-slate-600 mt-1">{obj.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {exercise.injects && exercise.injects.length > 0 && (
                <div>
                  <div className="font-bold text-slate-900 mb-1.5 uppercase tracking-wider text-[11px]">
                    Exercise Injects Timeline
                  </div>
                  <div className="space-y-2">
                    {exercise.injects.map((inj, i) => (
                      <div key={i} className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                        <div className="flex items-center justify-between font-bold text-slate-900">
                          <span>
                            Inject #{inj.inject_number}: {inj.title}
                          </span>
                          <span className="text-[10px] text-slate-500 font-normal">
                            +{inj.timestamp_offset_minutes}m
                          </span>
                        </div>
                        <p className="text-slate-600 mt-1">{inj.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {exercise.discussion_questions && exercise.discussion_questions.length > 0 && (
                <div>
                  <div className="font-bold text-slate-900 mb-1.5 uppercase tracking-wider text-[11px]">
                    Discussion Questions
                  </div>
                  <div className="space-y-2">
                    {exercise.discussion_questions.map((q, i) => (
                      <div key={i} className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                        <div className="font-semibold text-slate-900">{q.question}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4 mt-5 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsScenarioModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ExerciseDetail;
